import { useEffect, useState } from 'react';
import { ApiError } from '@/lib/api/types';
import type { FlightRow, ScheduleCriteria } from '@/types/flight';
import { fetchSchedule } from './api';

type ErrorKey = 'schedule.errorForbidden' | 'schedule.errorLoad';

/**
 * How often the board asks for the same page again while it is on screen.
 *
 * Half a minute, the same as the map: the board is left open on a screen for a whole
 * shift, and a new estimate or a gate change should appear without anybody touching
 * it. Faster would be a request every few seconds for a feed that itself only moves
 * every minute or so.
 */
const REFRESH_MS = 30_000;

/** What one completed request produced, tagged with the request it answered. */
interface Outcome {
  key: string;
  rows: FlightRow[];
  currPage: number;
  total: number;
  isLastPage: boolean;
  errorKey: ErrorKey | null;
}

interface ScheduleState {
  rows: FlightRow[];
  /** The page the server ACTUALLY returned, which is not always the one asked for. */
  currPage: number;
  total: number;
  isLastPage: boolean;
  isLoading: boolean;
  /** A translation key, so the message arrives in the reader's language. */
  errorKey: ErrorKey | null;
}

/**
 * Runs the table's query.
 *
 * **Loading is derived, not stored.** The only state here is the outcome of the last
 * completed request, tagged with the criteria it answered; "loading" is simply that
 * tag not matching what is being asked for now. Setting a loading flag at the top of
 * the effect — the obvious way to write this — sets state during the commit and
 * cascades an extra render, which is what React's own lint rules object to.
 *
 * It also gives the behaviour a board wants for free: the previous rows stay on
 * screen while the next page loads, so the reader can compare what they had against
 * what they asked for instead of watching the table blink empty.
 *
 * Two other things it is careful about:
 *
 * - **Stale answers are discarded.** Filters change a keystroke at a time; the tag
 *   means a slow request that lands after a newer one cannot paint over it.
 * - **A 403 is not a failure to retry.** `INTEGRATOR` and `OPERATOR` are refused on
 *   the read endpoints too, and telling them to check their connection would send
 *   them looking in the wrong place.
 *
 * **It keeps itself fresh.** The same query runs again every `REFRESH_MS`, under the
 * same key — so a refresh never counts as loading, and the table does not dim or show
 * the bar every half-minute. A hidden tab does not poll, and catches up the moment it
 * is shown again. A filter change restarts the clock, so a refresh never lands on the
 * heels of the load the reader just asked for.
 */
export const useSchedule = (criteria: ScheduleCriteria): ScheduleState => {
  // Serialized so the effect keys off the FILTER, not off the object identity —
  // which is new on every render.
  const key = JSON.stringify(criteria);

  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const query = JSON.parse(key) as ScheduleCriteria;
    let controller: AbortController | null = null;

    // Whether this query has put anything on screen yet — rows or an error.
    let settled = false;

    const load = () => {
      controller?.abort();
      const current = new AbortController();
      controller = current;

      fetchSchedule(query, current.signal)
        .then((response) => {
          settled = true;
          setOutcome({
            key,
            rows: Array.isArray(response.rows) ? response.rows : [],
            currPage: response.currPage ?? 1,
            total: response.rsCount ?? 0,
            // The server's own answer to "is there a next page", rather than a page
            // count computed from the total.
            isLastPage: response.lastBlock !== false,
            errorKey: null,
          });
        })
        .catch((caught: unknown) => {
          // An abort is not an error: a newer request is already in flight, and its
          // outcome is the one that should win.
          if (current.signal.aborted) return;

          // A failed REFRESH keeps what is on screen. Those rows are half a minute
          // old, not wrong, and blanking a wall screen because one poll dropped would
          // be worse than the staleness — the next tick tries again.
          if (settled) return;

          settled = true;
          setOutcome({
            key,
            rows: [],
            currPage: 1,
            total: 0,
            isLastPage: true,
            errorKey: caught instanceof ApiError && caught.isForbidden
              ? 'schedule.errorForbidden'
              : 'schedule.errorLoad',
          });
        });
    };

    const refresh = () => {
      if (!document.hidden) load();
    };

    load();
    const timer = window.setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      controller?.abort();
    };
  }, [key]);

  const isCurrent = outcome?.key === key;

  return {
    // The previous page's rows stay visible while the next one loads. On an error
    // they are cleared, because showing yesterday's answer under an error message
    // would be worse than showing none.
    rows: isCurrent || !outcome?.errorKey ? (outcome?.rows ?? []) : [],
    currPage: outcome?.currPage ?? 1,
    total: outcome?.total ?? 0,
    isLastPage: outcome?.isLastPage ?? true,
    isLoading: !isCurrent,
    errorKey: isCurrent ? outcome.errorKey : null,
  };
};
